import { useMutation, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'
import type { AppLocale } from '@/config/locale'

const nullableString = z.string().nullable().optional()

const teamSchema = z
  .object({
    id: z.number().optional(),
    name: nullableString,
    display_name: nullableString,
    short_name: nullableString,
    logo_url: nullableString,
    is_placeholder: z.boolean().optional(),
  })
  .passthrough()

const predictionSchema = z
  .object({
    home_pred: z.number().nullable().optional(),
    away_pred: z.number().nullable().optional(),
    points_awarded: z.number().nullable().optional(),
    virtual_points: z.number().nullable().optional(),
    poule_id: z.number().optional(),
  })
  .passthrough()

const predictionScopeSchema = z
  .object({
    mode: nullableString,
    applies_to_all: z.boolean().optional(),
    poule_count: z.number().optional(),
    poules: z
      .array(
        z
          .object({
            id: z.number().optional(),
            name: nullableString,
            thumbnail_url: nullableString,
          })
          .passthrough(),
      )
      .optional()
      .default([]),
  })
  .passthrough()

const deduplicatedMatchSchema = z
  .object({
    match_id: z.number(),
    kickoff: nullableString,
    home_team: teamSchema.optional(),
    away_team: teamSchema.optional(),
    user_prediction: predictionSchema.nullable().optional(),
    prediction_scope: predictionScopeSchema.optional(),
    is_predictable: z.boolean().optional(),
    predict_status: nullableString,
    is_cancelled: z.boolean().optional(),
    representative_poule_id: z.number().optional(),
    home_pred: z.number().nullable().optional(),
    away_pred: z.number().nullable().optional(),
    points_awarded: z.number().nullable().optional(),
    virtual_points: z.number().nullable().optional(),
    poule_id: z.number().optional(),
  })
  .passthrough()

const pouleMatchSchema = z
  .object({
    id: z.number(),
    season_id: z.number().optional(),
    league_id: z.number().optional(),
    kickoff_utc: nullableString,
    kickoff_nl: nullableString,
    status: nullableString,
    poule_match_id: z.number().optional(),
    is_predictable: z.boolean().optional(),
    predict_status: nullableString,
    is_cancelled: z.boolean().optional(),
    prediction_locked_by_stage_release: z.boolean().optional(),
    prediction_locked_reason: nullableString,
    round: z.union([z.number(), nullableString]).optional(),
    joker: z.boolean().optional(),
    joker_multiplier: z.number().optional(),
    kickoff_time_tbd: z.boolean().optional(),
    minute: z.number().nullable().optional(),
    extra_minute: z.number().nullable().optional(),
    home_team: teamSchema.optional(),
    away_team: teamSchema.optional(),
    score: z
      .object({
        home: z.number().nullable().optional(),
        away: z.number().nullable().optional(),
      })
      .passthrough()
      .optional(),
    home_form: z.array(nullableString).optional().default([]),
    away_form: z.array(nullableString).optional().default([]),
    stage_name: nullableString,
    user_prediction: predictionSchema.nullable().optional(),
    home_pred: z.number().nullable().optional(),
    away_pred: z.number().nullable().optional(),
    points_awarded: z.number().nullable().optional(),
    virtual_points: z.number().nullable().optional(),
    poule_id: z.number().optional(),
  })
  .passthrough()

const liveMatchesSchema = z
  .object({
    matches_count: z.number().optional(),
    has_live_matches: z.boolean().optional(),
    poules: z
      .array(
        z
          .object({
            id: z.number(),
            name: z.string(),
            poule_type: nullableString,
            scope_type: nullableString,
            thumbnail_url: nullableString,
            prediction_deadline_context: z.record(z.string(), z.unknown()).optional(),
            matches: z.array(pouleMatchSchema).optional().default([]),
          })
          .passthrough(),
      )
      .optional()
      .default([]),
  })
  .passthrough()

const nextMatchesSchema = z
  .object({
    date: nullableString,
    minimum_date: nullableString,
    previous_date: nullableString,
    next_date: nullableString,
    can_go_previous: z.boolean().optional(),
    can_go_next: z.boolean().optional(),
    selected_tab_id: nullableString,
    tabs: z
      .array(
        z
          .object({
            id: z.string(),
            type: nullableString,
            date: nullableString,
            label: nullableString,
            short_label: nullableString,
            match_count: z.number().optional(),
            has_unpredicted: z.boolean().optional(),
            is_selected: z.boolean().optional(),
            is_today: z.boolean().optional(),
            is_live: z.boolean().optional(),
            is_completed: z.boolean().optional(),
          })
          .passthrough(),
      )
      .optional()
      .default([]),
    deduplicated_matches: z
      .object({
        visible: z.boolean().optional(),
        mode: nullableString,
        items: z.array(deduplicatedMatchSchema).optional().default([]),
      })
      .passthrough()
      .optional(),
    poules: z
      .array(
        z
          .object({
            id: z.number(),
            name: z.string(),
            poule_type: nullableString,
            scope_type: nullableString,
            thumbnail_url: nullableString,
            prediction_deadline_context: z.record(z.string(), z.unknown()).optional(),
            matches: z.array(pouleMatchSchema).optional().default([]),
          })
          .passthrough(),
      )
      .optional()
      .default([]),
    match_prediction_sync_hint: z
      .object({
        visible: z.boolean().optional(),
        season_id: z.number().nullable().optional(),
        competition_name: nullableString,
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const savedPredictionSchema = z
  .object({
    home_pred: z.number().nullable().optional(),
    away_pred: z.number().nullable().optional(),
    poule_id: z.number().optional(),
    points_awarded: z.number().nullable().optional(),
    virtual_points: z.number().nullable().optional(),
  })
  .passthrough()

export interface NextMatchesParams {
  date?: string | undefined
  enabled?: boolean
  locale: AppLocale
}

export interface LiveMatchesParams {
  enabled?: boolean
  locale: AppLocale
}

export interface SavePredictionInput {
  matchId: number
  pouleId: number
  homePred: number
  awayPred: number
}

export type NextMatchesResponse = z.infer<typeof nextMatchesSchema>
export type LiveMatchesResponse = z.infer<typeof liveMatchesSchema>
export type SavedPrediction = z.infer<typeof savedPredictionSchema>

async function fetchNextMatches(
  paramsInput: Pick<NextMatchesParams, 'locale' | 'date'>,
): Promise<NextMatchesResponse> {
  const params = new URLSearchParams()
  if (paramsInput.date) params.set('date', paramsInput.date)
  // The SPA currently renders the regular poule groups and does not use the
  // optional duplicate-match presentation. These flags are additive: older
  // clients still receive the original backend response.
  params.set('include_deduplicated', '0')
  params.set('exclude_empty_poules', '1')

  const path = `/users/me/next-matches?${params.toString()}`
  const response = await api.get<unknown>(path)
  const parsed = nextMatchesSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[match-center] Invalid next-matches payload', parsed.error.flatten())
    throw new Error('Wedstrijden konden niet worden geladen.')
  }

  return parsed.data
}

export function useNextMatches(params: NextMatchesParams) {
  const { date, enabled = true, locale } = params

  return useQuery({
    queryKey: ['next-matches', locale, date ?? 'today'],
    queryFn: () => fetchNextMatches({ locale, date }),
    enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

async function fetchLiveMatches(
  paramsInput: Pick<LiveMatchesParams, 'locale'>,
): Promise<LiveMatchesResponse> {
  const response = await api.get<unknown>('/poules/matches/live')
  const parsed = liveMatchesSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[match-center] Invalid live-matches payload', parsed.error.flatten())
    throw new Error('Live wedstrijden konden niet worden geladen.')
  }

  return parsed.data
}

export function useLiveMatches(params: LiveMatchesParams) {
  const { enabled = true, locale } = params

  return useQuery({
    queryKey: ['live-matches', locale],
    queryFn: () => fetchLiveMatches({ locale }),
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled ? 30_000 : false,
  })
}

async function savePrediction(input: SavePredictionInput): Promise<SavedPrediction> {
  const response = await api.post<unknown>(
    `/matches/${input.matchId}/predictions`,
    {
      poule_id: input.pouleId,
      home_pred: input.homePred,
      away_pred: input.awayPred,
      home_mvp: null,
      away_mvp: null,
    },
  )

  const parsed = savedPredictionSchema.safeParse(response)
  if (!parsed.success) {
    console.error('[match-center] Invalid prediction save payload', parsed.error.flatten())
    throw new Error('Voorspelling kon niet worden opgeslagen.')
  }

  return parsed.data
}

export function useSavePrediction() {
  return useMutation({
    mutationFn: savePrediction,
  })
}
